import React, { useState, useMemo } from "react";
import {
    Box,
    Typography,
    Grid2,
    Card,
    FormControlLabel,
    Link,
    TextField,
    styled,
    CircularProgress
} from "@mui/material";
import { PrimaryFormLoadingActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CustomIcon from "../../../Components/CustomIcon";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import CustomCheckbox from "../../BmsFaults/Components/CustomCheckbox";
import ErrorModal from "../Components/ErrorModal";
import { downloadFullReport, downloadErrorReport } from "../../../Api/ResourceOnboardingApi";
import DownloadCSV from "../../../Helper/CSVOperations/DownloadCSV";

// Custom styled TextField
const StyledTextField = styled(TextField)((theme) => ({
    "& .MuiOutlinedInput-root": {
        fontSize: "12px",
        lineHeight: "24px",
        padding: 0,
        backgroundColor: '#FFFFFF',

        "& fieldset": {
            borderColor: "#E6E6E6",
            borderWidth: "1px",
            borderRadius: '8px',
        },
        "&:hover fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "& textarea": {
            padding: '8px 16px',
            fontSize: "14px",
            lineHeight: "20px",
        },
        "& textarea::placeholder": {
            color: "#A4B2C1",
            opacity: 1,
        },
    },
    '& .MuiList-root': {
        padding: 0,
    }
}));

const Step4ValidateData = ({ selectedResourceType, validationData, importUUID, onUpload, onBackToTable }) => {
    const [finalRemark, setFinalRemark] = useState("");
    const [agreeNewRecords, setAgreeNewRecords] = useState(false);
    const [agreeUpdateRecords, setAgreeUpdateRecords] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [downloadingFull, setDownloadingFull] = useState(false);
    const [downloadingError, setDownloadingError] = useState(false);

    // Extract validation summary from API response
    const validationSummary = useMemo(() => {
        const responseData = validationData?.response_data || {};
        return {
            totalRecords: responseData.numTotalRecords || 0,
            creatingNewRecords: responseData.numCreatedRecords || 0,
            updatingExistingRecords: responseData.numUpdatedRecords || 0,
            invalidRecords: responseData.numFailedRecords || 0
        };
    }, [validationData]);

    // Extract error report from API response
    const validationErrors = useMemo(() => {
        const errorReport = validationData?.error_report || [];
        return errorReport.map(error => ({
            validationCheck: error.errorCode || "-",
            failed: error.failedRecords || 0,
            details: error.errorDescription || "-"
        }));
    }, [validationData]);

    const validRecordsCount = validationSummary.totalRecords - validationSummary.invalidRecords;

    // Configure table columns for error report
    const errorTableColumns = useMemo(() => {
        const tableColumns = {
            validationCheck: {
                key: 'validationCheck',
                title: 'Validation Check',
                width: 'auto',
                sorter: false,
                dataIndex: 'validationCheck',
                defaultValue: "-",
                valueRenderingType: "text",
                fixed: "left"
            },
            failed: {
                key: 'failed',
                title: 'Failed',
                width: 'auto',
                sorter: false,
                dataIndex: 'failed',
                defaultValue: "-",
                valueRenderingType: "failed_custom"
            },
            details: {
                key: 'details',
                title: 'Details',
                width: 'auto',
                sorter: false,
                dataIndex: 'details',
                defaultValue: "-",
                valueRenderingType: "text"
            }
        };

        const customRenderers = {
            failed_custom: (record, config) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelOutlinedIcon sx={{
                        color: '#B50500',
                        fontSize: '16px'
                    }} />
                    <Typography variant="fw400s14c000000" sx={{ color: '#2E2E2E' }}>
                        {record?.failed}
                    </Typography>
                </Box>
            )
        };

        const columnsConfig = getColumnsConfig({
            tableColumns,
            tableColumnOrder: ['validationCheck', 'failed', 'details'],
            customRenderers
        });

        return columnsConfig;
    }, []);

    const errorTableData = useMemo(() => {
        return validationErrors.map((error, index) => ({
            ...error,
            key: `error-${index}`
        }));
    }, [validationErrors]);

    const handleUpload = async () => {
        if (onUpload) {
            try {
                setIsUploading(true);
                await onUpload({
                    remark: finalRemark,
                    validRecordsCount,
                    totalRecords: validationSummary.totalRecords
                });
            } catch (error) {
                console.error('Upload error:', error);
                setErrorMessage(error.message || 'An unexpected error occurred. Please try again.');
                setShowErrorModal(true);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setErrorMessage('');
    };

    const handleDownloadFullReport = async (e) => {
        e.preventDefault();
        if (!importUUID || downloadingFull) return;

        try {
            setDownloadingFull(true);
            const response = await downloadFullReport(importUUID);

            // Check if response is blob data (file download)
            if (response?.response instanceof Blob) {
                const url = window.URL.createObjectURL(response?.response);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedResourceType?.label || 'Resource'}_Full_Report_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Fallback to CSV data format
                const csvData = response?.data || [];
                if (csvData.length > 0) {
                    const filename = `${selectedResourceType?.label || 'Resource'}_Full_Report_${new Date().toISOString().split('T')[0]}.csv`;
                    DownloadCSV(filename, csvData);
                }
            }
        } catch (error) {
            console.error("Error downloading full report:", error);
        } finally {
            setDownloadingFull(false);
        }
    };

    const handleDownloadErrorReport = async (e) => {
        e.preventDefault();
        if (!importUUID || downloadingError || validationSummary.invalidRecords === 0) return;

        try {
            setDownloadingError(true);
            const response = await downloadErrorReport(importUUID);

            // Check if response is blob data (file download)
            if (response?.response instanceof Blob) {
                const url = window.URL.createObjectURL(response?.response);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedResourceType?.label || 'Resource'}_Error_Report_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Fallback to CSV data format
                const csvData = response?.data || [];
                if (csvData.length > 0) {
                    const filename = `${selectedResourceType?.label || 'Resource'}_Error_Report_${new Date().toISOString().split('T')[0]}.csv`;
                    DownloadCSV(filename, csvData);
                }
            }
        } catch (error) {
            console.error("Error downloading error report:", error);
        } finally {
            setDownloadingError(false);
        }
    };

    const isUploadDisabled = !finalRemark || !agreeNewRecords || !agreeUpdateRecords || isUploading;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3.5 }}>
                <Typography variant="fw500s20c000000" sx={{ lineHeight: '20px' }}>
                    {selectedResourceType?.label || 'Resource Onboarding'}
                </Typography>
            </Box>

            {/* Summary Section */}
            <Grid2 container flexDirection={'column'} gap={1} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="fw500s16c000000" sx={{ lineHeight: '16px' }}>
                        Summary
                    </Typography>
                    <Link
                        href="#"
                        underline="always"
                        onClick={handleDownloadFullReport}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#5A5FCD',
                            fontSize: '14px',
                            fontWeight: 500,
                            textDecorationColor: '#5A5FCD',
                            cursor: downloadingFull ? 'wait' : 'pointer',
                            opacity: downloadingFull ? 0.6 : 1,
                            pointerEvents: downloadingFull || !importUUID ? 'none' : 'auto'
                        }}
                    >
                        {downloadingFull ? (
                            <CircularProgress size={16} sx={{ color: '#5A5FCD' }} />
                        ) : (
                            <FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />
                        )}
                        Download Full Data Report
                    </Link>
                </Box>
            </Grid2>

            {/* Summary Cards */}
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
                <Grid2 size={3}>
                    <Card sx={{
                        py: 2,
                        px: 3,
                        backgroundColor: '#F9F9F9',
                        boxShadow: 'none',
                        borderRadius: '8px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <CustomIcon name="database-icon" stroke="#4D44D8" width="20" height="20" />
                            <Typography variant="fw600s14c000000" sx={{ color: '#2E2E2E' }}>
                                Total Records
                            </Typography>
                        </Box>
                        <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', color: '#4D44D8' }}>
                            {validationSummary.totalRecords}
                        </Typography>
                    </Card>
                </Grid2>

                <Grid2 size={3}>
                    <Card sx={{
                        py: 2,
                        px: 3,
                        backgroundColor: '#F9F9F9',
                        boxShadow: 'none',
                        borderRadius: '8px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <CheckCircleOutlineRoundedIcon sx={{ color: '#008A15', fontSize: '20px' }} />
                            <Typography variant="fw600s14c000000" sx={{ color: '#2E2E2E' }}>
                                Creating New Records
                            </Typography>
                        </Box>
                        <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', color: '#008A15' }}>
                            {validationSummary.creatingNewRecords}
                        </Typography>
                    </Card>
                </Grid2>

                <Grid2 size={3}>
                    <Card sx={{
                        py: 2,
                        px: 3,
                        backgroundColor: '#F9F9F9',
                        boxShadow: 'none',
                        borderRadius: '8px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <CheckCircleOutlineRoundedIcon sx={{ color: '#008A15', fontSize: '20px' }} />
                            <Typography variant="fw600s14c000000" sx={{ color: '#2E2E2E' }}>
                                Updating Existing Records
                            </Typography>
                        </Box>
                        <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', color: '#008A15' }}>
                            {validationSummary.updatingExistingRecords}
                        </Typography>
                    </Card>
                </Grid2>

                <Grid2 size={3}>
                    <Card sx={{
                        py: 2,
                        px: 3,
                        backgroundColor: '#F9F9F9',
                        boxShadow: 'none',
                        borderRadius: '8px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <CancelOutlinedIcon sx={{ color: '#B50500', fontSize: '20px' }} />
                            <Typography variant="fw600s14c000000" sx={{ color: '#2E2E2E' }}>
                                Invalid Records
                            </Typography>
                        </Box>
                        <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', color: '#B50500' }}>
                            {validationSummary.invalidRecords}
                        </Typography>
                    </Card>
                </Grid2>
            </Grid2>

            {/* Error Report Section */}
            <Grid2 container flexDirection={'column'} gap={1} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="fw500s16c000000" sx={{ lineHeight: '16px' }}>
                        Error Report
                    </Typography>
                    <Link
                        href="#"
                        underline="always"
                        onClick={handleDownloadErrorReport}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#5A5FCD',
                            fontSize: '14px',
                            fontWeight: 500,
                            textDecorationColor: '#5A5FCD',
                            cursor: downloadingError ? 'wait' : 'pointer',
                            opacity: downloadingError || validationSummary.invalidRecords === 0 ? 0.6 : 1,
                            pointerEvents: downloadingError || !importUUID || validationSummary.invalidRecords === 0 ? 'none' : 'auto'
                        }}
                    >
                        {downloadingError ? (
                            <CircularProgress size={16} sx={{ color: '#5A5FCD' }} />
                        ) : (
                            <FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />
                        )}
                        Download Error Report
                    </Link>
                </Box>
            </Grid2>

            {/* Error Table */}
            <Box sx={{ mb: 3, width: "calc(100vw - 169px)" }}>
                <GenericDraggableTableView
                    tableID='ValidationErrorReportList'
                    tableColumns={errorTableColumns}
                    tableData={errorTableData}
                    initialColumns={errorTableColumns}
                    showPrimaryButton={false}
                    disableDownloads={true}
                    disablePagination={true}
                    disableSearch={true}
                    disableFiltersSettings={true}
                    disableColumnSettings={true}
                    isLoading={false}
                    numberOfFixedColumns={1}
                    disableTableHeader={true}
                    emptyMessage="No error exists in this import"
                />
            </Box>

            {/* Final Remark */}
            <Box sx={{ mb: 2, bgcolor: '#F9F9F9', padding: '16px 24px', borderRadius: '8px' }}>
                <Grid2 container mb={1} alignItems={'center'} gap={1.5}>
                    <CustomIcon name="comment-icon" fill="#A4B2C1" />
                    <Box>
                        <Typography variant="fw700s14c3D4B5A" sx={{ color: '#000000', lineHeight: '24px' }}>Add Final Remark</Typography>
                        <Typography variant="fw700s14c3D4B5A" sx={{ color: '#B50500', lineHeight: '24px' }}>&nbsp;*</Typography>
                    </Box>
                </Grid2>
                <Grid2 container gap={0.5} flexDirection={'column'}>
                    <StyledTextField
                        fullWidth
                        autoComplete="off"
                        variant="outlined"
                        placeholder="Type here"
                        value={finalRemark}
                        onChange={(e) => setFinalRemark(e.target.value)}
                        multiline
                        minRows={5}
                        sx={{ flex: 1, minWidth: "150px" }}
                    // error={errors['newComment'] || newComment.length > MAX_COMMENT_LENGTH}
                    />
                    {/* {errors['newComment'] && (
                        <FormHelperText sx={{ fontSize: '10px', color: '#930500', ml: 0, mt: -0.5 }}>{'Please enter comments'}</FormHelperText>
                    )} */}
                    {/* <Typography
                        variant="fw400s10cB50500"
                        sx={{ color: newComment.length > MAX_COMMENT_LENGTH ? '#930500 !important' : '#8D8D8D !important' }}
                    >
                        Only 250 characters are allowed: {newComment.length}/{MAX_COMMENT_LENGTH} characters
                    </Typography> */}
                </Grid2>
            </Box>

            {/* Ready for Upload Section */}
            <Card sx={{
                py: 2.5,
                px: 3.5,
                backgroundColor: '#EDF9F4',
                border: '1px solid #09B820',
                borderRadius: '8px',
                boxShadow: 'none',
                mb: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircleOutlineRoundedIcon sx={{ color: '#008A15', fontSize: '20px' }} />
                    <Typography variant="fw600s16c000000" sx={{ color: '#008A15' }}>
                        Ready for Upload
                    </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="fw400s14c000000">
                        <span style={{ fontWeight: '700' }}>{validRecordsCount}/{validationSummary.totalRecords}</span> records have passed validation and are ready to be uploaded. Invalid records will be skipped
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                            // <Checkbox
                            //     checked={agreeNewRecords}
                            //     onChange={(e) => setAgreeNewRecords(e.target.checked)}
                            //     sx={{
                            //         color: '#A2A2A2',
                            //         py: 0,
                            //         '&.Mui-checked': {
                            //             color: '#008A15',
                            //         }
                            //     }}
                            // />
                            <CustomCheckbox
                                variant="light"
                                checked={agreeNewRecords}
                                onChange={(e) => setAgreeNewRecords(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="fw400s14c000000" sx={{ color: '#494949', ml: 1 }}>
                                I agree that {validationSummary.creatingNewRecords}/{validRecordsCount} valid records are new and will be inserted in the platform.
                                <span style={{ color: '#B50500' }}>*</span>
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                            // <Checkbox
                            //     checked={agreeUpdateRecords}
                            //     onChange={(e) => setAgreeUpdateRecords(e.target.checked)}
                            //     sx={{
                            //         color: '#A2A2A2',
                            //         py: 0,
                            //         '&.Mui-checked': {
                            //             color: '#008A15',
                            //         }
                            //     }}
                            // />
                            <CustomCheckbox
                                variant="light"
                                checked={agreeUpdateRecords}
                                onChange={(e) => setAgreeUpdateRecords(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="fw400s14c000000" sx={{ color: '#494949', ml: 1 }}>
                                I agree that {validationSummary.updatingExistingRecords}/{validRecordsCount} valid records are mapped to existing data and will be update the current data on the platform.
                                <span style={{ color: '#B50500' }}>*</span>
                            </Typography>
                        }
                    />
                </Box>
            </Card>

            {/* Action Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PrimaryFormLoadingActionButton
                    disabled={isUploadDisabled}
                    onClick={handleUpload}
                    loading={isUploading}
                    sx={{ borderRadius: '10px', p: 2 }}
                >
                    Upload Valid Data
                </PrimaryFormLoadingActionButton>
            </Box>

            {/* Error Modal */}
            <ErrorModal
                open={showErrorModal}
                onClose={handleCloseErrorModal}
                errorMessage={errorMessage}
                buttonText="Go Back to Uploads"
                onButtonClick={onBackToTable}
            />
        </Box>
    );
};

export default Step4ValidateData;
